/**
 * Email subscription groups (SendGrid ASM) admin catalog.
 *
 * Lists the CRM-owned catalog of approved SendGrid unsubscribe groups and lets
 * admin/it users add / edit / (de)activate them. Numeric group IDs come from the
 * business's SendGrid account (created there, imported here). Read-only for other
 * authenticated roles — writes are gated by RLS and hidden in the UI.
 */
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../components/ui/Toast";
import Button from "../components/common/Button";
import LoadingSpinner from "../components/common/LoadingSpinner";
import {
  createGroup,
  listGroups,
  setActive,
  type SubscriptionGroup,
  type SubscriptionGroupInput,
  updateGroup,
} from "../services/subscriptionGroups.service";

const EMPTY: SubscriptionGroupInput = {
  name: "",
  slug: "",
  sendgrid_group_id: 0,
  description: "",
  is_active: true,
  display_on_preferences: true,
  is_default_marketing: false,
  sort_order: 0,
};

const Badge: React.FC<{
  tone: "green" | "gray" | "blue" | "amber";
  children: React.ReactNode;
}> = ({ tone, children }) => {
  const cls = {
    green: "bg-green-100 text-green-800",
    gray: "bg-gray-100 text-gray-600",
    blue: "bg-blue-100 text-blue-800",
    amber: "bg-amber-100 text-amber-800",
  }[tone];
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs ${cls}`}>
      {children}
    </span>
  );
};

const EmailSubscriptionGroups: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const canWrite = ["super_admin", "superadmin", "admin", "it"].includes(
    (user?.role || "").toLowerCase(),
  );

  const {
    data: groups,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["email-subscription-groups"],
    queryFn: listGroups,
  });

  const [editing, setEditing] = useState<SubscriptionGroup | null>(null);
  const [form, setForm] = useState<SubscriptionGroupInput>(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const warnings = useMemo(() => {
    const out: string[] = [];
    const active = (groups ?? []).filter((g) => g.is_active);
    // No "default marketing category" warning: there is no default-fallback resolver
    // (audit R2-secondary) — an unassigned marketing send is governed by the enforcement
    // flag, not a default category, so surfacing a default would set a false expectation.
    if (active.filter((g) => g.display_on_preferences).length > 25) {
      out.push(
        "More than 25 active groups are set to show on the preferences page — SendGrid displays at most 25.",
      );
    }
    return out;
  }, [groups]);

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY);
    setShowForm(true);
  };
  const openEdit = (g: SubscriptionGroup) => {
    setEditing(g);
    setForm({
      name: g.name,
      slug: g.slug,
      sendgrid_group_id: g.sendgrid_group_id,
      description: g.description ?? "",
      is_active: g.is_active,
      display_on_preferences: g.display_on_preferences,
      is_default_marketing: g.is_default_marketing,
      sort_order: g.sort_order,
      provider_account: g.provider_account ?? "",
    });
    setShowForm(true);
  };

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["email-subscription-groups"] });

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await updateGroup(editing.id, form);
        showToast("success", "Subscription category updated.");
      } else {
        await createGroup(form);
        showToast("success", "Subscription category added.");
      }
      setShowForm(false);
      await refresh();
    } catch (e) {
      showToast("error", e instanceof Error ? e.message : "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (g: SubscriptionGroup) => {
    try {
      await setActive(g.id, !g.is_active);
      showToast(
        "success",
        g.is_active ? "Category deactivated." : "Category reactivated.",
      );
      await refresh();
    } catch (e) {
      showToast("error", e instanceof Error ? e.message : "Could not update.");
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-5 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Email Subscription Categories
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Approved SendGrid unsubscribe groups. Create the group in SendGrid
            first, then add its numeric ID here so flights and sequences can be
            associated to it.
          </p>
        </div>
        {canWrite && (
          <Button variant="primary" size="sm" onClick={openAdd}>
            Add category
          </Button>
        )}
      </div>

      {warnings.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 space-y-1">
          {warnings.map((w) => (
            <div key={w}>⚠️ {w}</div>
          ))}
        </div>
      )}

      {showForm && canWrite && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
          <div className="font-medium text-gray-900">
            {editing ? "Edit category" : "Add category"}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-sm">
              <span className="block text-gray-700 mb-1">Name</span>
              <input
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </label>
            <label className="text-sm">
              <span className="block text-gray-700 mb-1">Slug</span>
              <input
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                placeholder="events-training"
              />
            </label>
            <label className="text-sm">
              <span className="block text-gray-700 mb-1">
                SendGrid group ID
              </span>
              <input
                type="number"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100 disabled:text-gray-500"
                value={form.sendgrid_group_id || ""}
                disabled={!!editing}
                onChange={(e) =>
                  setForm({
                    ...form,
                    sendgrid_group_id: parseInt(e.target.value, 10) || 0,
                  })
                }
              />
              {editing && (
                <span className="mt-1 block text-xs text-gray-500">
                  The SendGrid group ID can't be changed. To point this category
                  at a different group, deactivate it and add a new category.
                </span>
              )}
            </label>
            <label className="text-sm">
              <span className="block text-gray-700 mb-1">Sort order</span>
              <input
                type="number"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                value={form.sort_order ?? 0}
                onChange={(e) =>
                  setForm({
                    ...form,
                    sort_order: parseInt(e.target.value, 10) || 0,
                  })
                }
              />
            </label>
            <label className="text-sm md:col-span-2">
              <span className="block text-gray-700 mb-1">
                Description (shown to recipients)
              </span>
              <input
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                value={form.description ?? ""}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
              />
            </label>
          </div>
          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.is_active ?? true}
                onChange={(e) =>
                  setForm({ ...form, is_active: e.target.checked })
                }
              />
              Active
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.display_on_preferences ?? true}
                onChange={(e) =>
                  setForm({ ...form, display_on_preferences: e.target.checked })
                }
              />
              Show on preferences page
            </label>
          </div>
          <div className="flex gap-2 pt-1">
            <Button
              variant="primary"
              size="sm"
              onClick={save}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save"}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowForm(false)}
              disabled={saving}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {error ? (
        <div className="rounded-md bg-red-50 border border-red-200 p-4 text-sm text-red-700">
          Could not load categories:{" "}
          {error instanceof Error ? error.message : "unknown error"}.
        </div>
      ) : isLoading ? (
        <div className="py-6">
          <LoadingSpinner />
        </div>
      ) : (groups ?? []).length === 0 ? (
        <div className="text-center py-8 text-sm text-gray-500">
          No subscription categories yet.
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Slug</th>
                <th className="px-3 py-2">SendGrid ID</th>
                <th className="px-3 py-2">Flags</th>
                {canWrite && <th className="px-3 py-2 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {(groups ?? []).map((g) => (
                <tr key={g.id} className="text-gray-700">
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-900">{g.name}</div>
                    {g.description && (
                      <div className="text-xs text-gray-400">
                        {g.description}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{g.slug}</td>
                  <td className="px-3 py-2">{g.sendgrid_group_id}</td>
                  <td className="px-3 py-2 space-x-1 whitespace-nowrap">
                    {g.is_active ? (
                      <Badge tone="green">Active</Badge>
                    ) : (
                      <Badge tone="gray">Inactive</Badge>
                    )}
                    {g.is_default_marketing && (
                      <Badge tone="blue">Default</Badge>
                    )}
                    {g.display_on_preferences && (
                      <Badge tone="amber">Preferences</Badge>
                    )}
                  </td>
                  {canWrite && (
                    <td className="px-3 py-2 text-right whitespace-nowrap space-x-2">
                      <button
                        className="text-primary-700 hover:underline text-xs"
                        onClick={() => openEdit(g)}
                      >
                        Edit
                      </button>
                      <button
                        className="text-gray-500 hover:underline text-xs"
                        onClick={() => toggleActive(g)}
                      >
                        {g.is_active ? "Deactivate" : "Reactivate"}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EmailSubscriptionGroups;
